// @flow
import React, { useContext, useEffect, useRef, useState, type Node } from 'react';
import { debounce } from 'lodash';
import classnames from 'classnames';

import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { ImmersiveSlideShowControls } from 'Components/widgets/immersive-slideshow/controls/ImmersiveSlideShowControls';

type Props = {
  className?: string,
  items: Array<Node>,
  nextLabel?: string,
  previousLabel?: string,
};

const PRELOADED_ITEMS_NB = 3;

function getInitialDisplayedIndex(length: number) {
  return Array(length)
    .fill(false)
    .fill(true, 0, PRELOADED_ITEMS_NB);
}

export function ImmersiveSlideShowNarrow(props: Props) {
  const { className, items = [], nextLabel, previousLabel } = props;

  const [activeIndex, setActiveIndex] = useState(0);
  const [displayedIndex, setDisplayedIndex] = useState(getInitialDisplayedIndex(items.length));

  const { isRTL } = useContext(ResponsiveContext);

  const coef = isRTL ? -1 : 1;

  const containerRef = useRef(null);

  const scroll = (index, e) => {
    if (e) {
      e.preventDefault();
    }

    if (containerRef && containerRef.current) {
      containerRef.current.scrollTo({
        behavior: 'smooth',
        left: coef * containerRef.current.scrollWidth * (index / items.length),
      });
    }
    setActiveIndex(index);
  };

  const onScroll = debounce(() => {
    if (containerRef && containerRef.current) {
      const pos = Math.round(
        ((coef * containerRef.current.scrollLeft) / containerRef.current.scrollWidth) *
          items.length,
      );
      setActiveIndex(pos);
    }
  }, 150);

  useEffect(
    () => {
      // $FlowFixMe
      containerRef.current.addEventListener('scroll', onScroll);

      return () => {
        // $FlowFixMe
        containerRef.current.removeEventListener('scroll', onScroll);
      };
    },
    [coef],
  );

  useEffect(
    () => {
      if (displayedIndex.every(Boolean)) return;

      const newDisplayedIndex = displayedIndex.map(
        (_, idx) => idx < activeIndex + PRELOADED_ITEMS_NB,
      );
      setDisplayedIndex(newDisplayedIndex);
    },
    [activeIndex],
  );

  if (!items.length) return null;

  return (
    <div className={classnames(className, 'overflow-hidden relative')}>
      <div
        className={classnames(
          className,
          'flex flex-row flex-no-wrap overflow-auto scroll-snap-type-x-mandatory w-full',
        )}
        ref={containerRef}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {items.map((item, idx) => (
          <div className="flex-none overflow-visible scroll-snap-align-center w-full" key={idx}>
            {displayedIndex[idx] ? item : null}
          </div>
        ))}
      </div>
      {items.length > 1 && (
        <ImmersiveSlideShowControls
          className={isRTL ? 'rotate-180' : ''}
          style={{ bottom: '50px' }}
          isNextDisabled={isRTL ? activeIndex === 0 : activeIndex === items.length - 1}
          isPrevDisabled={isRTL ? activeIndex === items.length - 1 : activeIndex === 0}
          nextLabel={nextLabel}
          onNext={e => scroll(activeIndex + coef, e)}
          onPrev={e => scroll(activeIndex - coef, e)}
          previousLabel={previousLabel}
        />
      )}
    </div>
  );
}
